import { useMemo, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {
  maquinaCartaoService,
  taxaMaquinaCartaoService,
} from "../../services/apiService";
import { useQuery } from "../../hooks/useQuery";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";
import {
  Button,
  FormField,
  IconButton,
  SelectInput,
  TextInput,
  buttonPageActiveClass,
  buttonPageClass,
  segmentedControlButtonActiveClass,
  segmentedControlButtonClass,
  segmentedControlButtonInactiveClass,
  segmentedControlClass,
} from "../../components/ui";
import { formatCurrencyMoeda, formatarValorReal } from "../../utils/FormatarValores";
import {
  competenciaParaInputMes,
  inputMesParaCompetencia,
  competenciaParaTimestamp,
  formatarCompetenciaMesAno,
} from "../../utils/FormatarCompetencia";

const MaquinaCartao = () => {
  const { showError, showSuccess } = useNotification();
  const confirmDialog = useConfirmDialog();

  const [abaAtiva, setAbaAtiva] = useState("maquinas");

  const [searchTerm, setSearchTerm] = useState("");
  const [paginacao, setPaginacao] = useState({ paginaAtual: 1, itensPorPagina: 5 });
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ nome: "" });
  const [editandoId, setEditandoId] = useState(null);

  const [filtrosTaxa, setFiltrosTaxa] = useState({
    maquinaCartaoId: "",
  });
  const [paginacaoTaxa, setPaginacaoTaxa] = useState({ paginaAtual: 1, itensPorPagina: 6 });
  const [showFormTaxa, setShowFormTaxa] = useState(false);
  const [formTaxa, setFormTaxa] = useState({
    maquinaCartaoId: "",
    valorTaxa: "",
    mesRef: "",
  });
  const [editandoTaxaId, setEditandoTaxaId] = useState(null);

  const [loading, setLoading] = useState(false);

  const {
    data: maquinasResponse,
    isLoading: isLoadingMaquinas,
    error: errorMaquinas,
    refetch: refetchMaquinas,
  } = useQuery(
    ["maquina-cartao", searchTerm],
    () => maquinaCartaoService.listar(searchTerm),
    {
      enabled: true,
      onError: (err) => {
        showError(err.message || "Erro ao carregar maquinas de cartao");
      },
    }
  );

  const maquinas = useMemo(
    () => (Array.isArray(maquinasResponse) ? maquinasResponse : []),
    [maquinasResponse]
  );

  const filtrosTaxaApi = useMemo(
    () => ({
      maquinaCartaoId: filtrosTaxa.maquinaCartaoId || undefined,
    }),
    [filtrosTaxa]
  );

  const {
    data: taxasResponse,
    isLoading: isLoadingTaxas,
    error: errorTaxas,
    refetch: refetchTaxas,
  } = useQuery(
    [
      "taxa-maquina-cartao",
      filtrosTaxaApi.maquinaCartaoId,
    ],
    () => taxaMaquinaCartaoService.listar(filtrosTaxaApi),
    {
      enabled: true,
      onError: (err) => {
        showError(err.message || "Erro ao carregar taxas");
      },
    }
  );

  const taxas = useMemo(
    () => (Array.isArray(taxasResponse) ? taxasResponse : []),
    [taxasResponse]
  );

  const maquinasFiltradas = useMemo(() => {
    if (!searchTerm.trim()) {
      return maquinas;
    }
    return maquinas.filter((m) =>
      String(m?.nome || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [maquinas, searchTerm]);

  const totalPaginas = Math.ceil(maquinasFiltradas.length / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const maquinasPaginadas = maquinasFiltradas.slice(
    indiceInicial,
    indiceInicial + paginacao.itensPorPagina
  );

  const taxasOrdenadas = useMemo(() => {
    return [...taxas].sort((a, b) => {
      const dataA = competenciaParaTimestamp(a?.mesRef);
      const dataB = competenciaParaTimestamp(b?.mesRef);
      return dataB - dataA;
    });
  }, [taxas]);

  const totalPaginasTaxa = Math.ceil(taxasOrdenadas.length / paginacaoTaxa.itensPorPagina);
  const indiceInicialTaxa = (paginacaoTaxa.paginaAtual - 1) * paginacaoTaxa.itensPorPagina;
  const indiceFinalTaxa = indiceInicialTaxa + paginacaoTaxa.itensPorPagina;
  const taxasPaginadas = taxasOrdenadas.slice(
    indiceInicialTaxa,
    indiceInicialTaxa + paginacaoTaxa.itensPorPagina
  );

  const gerarNumerosPaginas = (paginaAtual, totalPaginasLista) => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(1, paginaAtual - Math.floor(maxPaginasVisiveis / 2));
    let fim = Math.min(totalPaginasLista, inicio + maxPaginasVisiveis - 1);

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let pagina = inicio; pagina <= fim; pagina += 1) {
      paginas.push(pagina);
    }

    return paginas;
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const abrirForm = (maquina = null) => {
    if (maquina) {
      setEditandoId(maquina.id);
      setFormData({ nome: maquina.nome || "" });
    } else {
      setEditandoId(null);
      setFormData({ nome: "" });
    }
    setShowForm(true);
  };

  const fecharForm = () => {
    setShowForm(false);
    setEditandoId(null);
    setFormData({ nome: "" });
  };

  const salvarMaquina = async () => {
    if (!formData.nome.trim()) {
      showError("Nome da maquina e obrigatorio");
      return;
    }

    setLoading(true);
    try {
      if (editandoId) {
        await maquinaCartaoService.atualizar(editandoId, formData);
        showSuccess("Maquina de cartao atualizada com sucesso!");
      } else {
        await maquinaCartaoService.criar(formData);
        showSuccess("Maquina de cartao criada com sucesso!");
      }
      fecharForm();
      refetchMaquinas();
    } catch (err) {
      showError(err.message || "Erro ao salvar maquina de cartao");
    } finally {
      setLoading(false);
    }
  };

  const deletarMaquina = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Deseja deletar esta maquina de cartao?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      await maquinaCartaoService.deletar(id);
      showSuccess("Maquina de cartao deletada com sucesso!");
      refetchMaquinas();
    } catch (err) {
      showError(err.message || "Erro ao deletar maquina de cartao");
    } finally {
      setLoading(false);
    }
  };

  const abrirFormTaxa = (taxa = null) => {
    if (taxa) {
      setEditandoTaxaId(taxa.id);
      const valorTaxaNumero = Number(taxa?.valorTaxa);
      setFormTaxa({
        maquinaCartaoId: taxa?.maquinaCartaoId || "",
        valorTaxa:
          Number.isFinite(valorTaxaNumero)
            ? formatarValorReal((valorTaxaNumero * 100).toString()).formatted
            : "",
        mesRef: competenciaParaInputMes(taxa?.mesRef),
      });
    } else {
      setEditandoTaxaId(null);
      setFormTaxa({
        maquinaCartaoId: maquinas[0]?.id || "",
        valorTaxa: "",
        mesRef: "",
      });
    }
    setShowFormTaxa(true);
  };

  const fecharFormTaxa = () => {
    setShowFormTaxa(false);
    setEditandoTaxaId(null);
    setFormTaxa({
      maquinaCartaoId: "",
      valorTaxa: "",
      mesRef: "",
    });
  };

  const salvarTaxa = async () => {
    const valorTaxaNumerico =
      formTaxa.valorTaxa === "" ? null : formatarValorReal(formTaxa.valorTaxa).numeric;

    if (!formTaxa.maquinaCartaoId) {
      showError("Selecione uma maquina de cartao");
      return;
    }

    if (valorTaxaNumerico === null || Number.isNaN(valorTaxaNumerico) || valorTaxaNumerico < 0) {
      showError("Informe um valor de taxa valido");
      return;
    }

    if (!formTaxa.mesRef) {
      showError("Informe o mes de referencia");
      return;
    }

    const payload = {
      maquinaCartaoId: formTaxa.maquinaCartaoId,
      valorTaxa: valorTaxaNumerico,
      mesRef: inputMesParaCompetencia(formTaxa.mesRef),
    };

    setLoading(true);
    try {
      if (editandoTaxaId) {
        await taxaMaquinaCartaoService.atualizar(editandoTaxaId, payload);
        showSuccess("Taxa atualizada com sucesso!");
      } else {
        await taxaMaquinaCartaoService.criar(payload);
        showSuccess("Taxa criada com sucesso!");
      }
      fecharFormTaxa();
      refetchTaxas();
    } catch (err) {
      showError(err.message || "Erro ao salvar taxa");
    } finally {
      setLoading(false);
    }
  };

  const deletarTaxa = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Deseja deletar esta taxa?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      await taxaMaquinaCartaoService.deletar(id);
      showSuccess("Taxa deletada com sucesso!");
      refetchTaxas();
    } catch (err) {
      showError(err.message || "Erro ao deletar taxa");
    } finally {
      setLoading(false);
    }
  };

  const nomeMaquinaPorId = (id) => {
    const maquina = maquinas.find((item) => item.id === id);
    return maquina?.nome || "Nao informado";
  };

  return (
    <PageContainer className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold">Máquinas de Cartão</h1>
      </div>

      <div className={segmentedControlClass}>
        <Button
          onClick={() => setAbaAtiva("maquinas")}
          variant="segmented"
          className={`${segmentedControlButtonClass} ${
            abaAtiva === "maquinas"
              ? segmentedControlButtonActiveClass
              : segmentedControlButtonInactiveClass
          }`}
        >
          Máquinas
        </Button>
        <Button
          onClick={() => setAbaAtiva("taxas")}
          variant="segmented"
          className={`${segmentedControlButtonClass} ${
            abaAtiva === "taxas"
              ? segmentedControlButtonActiveClass
              : segmentedControlButtonInactiveClass
          }`}
        >
          Taxas
        </Button>
      </div>

      {abaAtiva === "maquinas" && (
        <>
          <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <TextInput
                type="text"
                placeholder="Pesquisar por nome..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => abrirForm()}
                variant="success"
                icon={Plus}
              >
                Nova Maquina
              </Button>
              <Button
                onClick={refetchMaquinas}
                icon={RefreshCw}
              >
                Buscar
              </Button>
            </div>
          </div>

          {isLoadingMaquinas && <p>Carregando maquinas de cartao...</p>}
          {errorMaquinas && <p className="text-red-500">{errorMaquinas.message}</p>}

          <div className="bg-white rounded-lg shadow border">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Lista de Máquinas</h3>
              <span className="text-sm text-gray-500">
                {maquinasFiltradas.length} registro(s) encontrado(s)
              </span>
            </div>

            <div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Taxas
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {maquinasPaginadas.length > 0 ? (
                    maquinasPaginadas.map((maquina) => (
                      <tr key={maquina.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{maquina.nome}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm text-gray-600">
                            {maquina.taxas?.length || 0} taxa(s)
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <IconButton
                              onClick={() => abrirForm(maquina)}
                              disabled={loading}
                              icon={Pencil}
                              variant="info"
                              label="Editar"
                            />
                            <IconButton
                              onClick={() => deletarMaquina(maquina.id)}
                              disabled={loading}
                              icon={Trash2}
                              variant="danger"
                              label="Excluir"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="p-8 text-center text-gray-500">
                        Nenhuma maquina de cartao encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPaginas > 1 && (
            <div className="px-6 py-4 border border-gray-200 rounded-lg bg-white">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Mostrando {indiceInicial + 1} ate {Math.min(indiceFinal, maquinasFiltradas.length)} de {maquinasFiltradas.length} registros
                </div>

                <div className="flex items-center space-x-2">
                  <IconButton
                    onClick={() => setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }))}
                    disabled={paginacao.paginaAtual === 1}
                    icon={ChevronsLeft}
                    label="Primeira página"
                  />

                  <IconButton
                    onClick={() =>
                      setPaginacao((prev) => ({
                        ...prev,
                        paginaAtual: Math.max(1, prev.paginaAtual - 1),
                      }))
                    }
                    disabled={paginacao.paginaAtual === 1}
                    icon={ChevronLeft}
                    label="Página anterior"
                  />

                  <div className="flex space-x-1">
                    {gerarNumerosPaginas(paginacao.paginaAtual, totalPaginas).map((numeroPagina) => (
                      <Button
                        key={numeroPagina}
                        onClick={() =>
                          setPaginacao((prev) => ({ ...prev, paginaAtual: numeroPagina }))
                        }
                        variant="ghost"
                        size="sm"
                        className={
                          paginacao.paginaAtual === numeroPagina
                            ? buttonPageActiveClass
                            : buttonPageClass
                        }
                      >
                        {numeroPagina}
                      </Button>
                    ))}
                  </div>

                  <IconButton
                    onClick={() =>
                      setPaginacao((prev) => ({
                        ...prev,
                        paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
                      }))
                    }
                    disabled={paginacao.paginaAtual === totalPaginas}
                    icon={ChevronRight}
                    label="Próxima página"
                  />

                  <IconButton
                    onClick={() => setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }))}
                    disabled={paginacao.paginaAtual === totalPaginas}
                    icon={ChevronsRight}
                    label="Última página"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {abaAtiva === "taxas" && (
        <>
          <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex-1 max-w-md">
              <SelectInput
                value={filtrosTaxa.maquinaCartaoId}
                onChange={(e) => {
                  setFiltrosTaxa((prev) => ({ ...prev, maquinaCartaoId: e.target.value }));
                  setPaginacaoTaxa((prev) => ({ ...prev, paginaAtual: 1 }));
                }}
              >
                <option value="">Todas as maquinas</option>
                {maquinas.map((maquina) => (
                  <option key={maquina.id} value={maquina.id}>
                    {maquina.nome}
                  </option>
                ))}
              </SelectInput>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => abrirFormTaxa()}
                variant="success"
                icon={Plus}
              >
                Nova Taxa
              </Button>
              <Button
                onClick={refetchTaxas}
                icon={RefreshCw}
              >
                Buscar
              </Button>
            </div>
          </div>

          {isLoadingTaxas && <p>Carregando taxas...</p>}
          {errorTaxas && <p className="text-red-500">{errorTaxas.message}</p>}

          <div className="bg-white rounded-lg shadow border">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Lista de Taxas</h3>
              <span className="text-sm text-gray-500">
                {taxasOrdenadas.length} registro(s) encontrado(s)
              </span>
            </div>

            <div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Maquina
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor da Taxa
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mês Ref
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {taxasPaginadas.length > 0 ? (
                    taxasPaginadas.map((taxa) => (
                      <tr key={taxa.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {taxa?.maquinaCartao?.nome || nomeMaquinaPorId(taxa?.maquinaCartaoId)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm text-gray-900">
                            {formatCurrencyMoeda(Number(taxa?.valorTaxa || 0))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm text-gray-900">
                            {formatarCompetenciaMesAno(taxa?.mesRef)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <IconButton
                              onClick={() => abrirFormTaxa(taxa)}
                              disabled={loading}
                              icon={Pencil}
                              variant="info"
                              label="Editar"
                            />
                            <IconButton
                              onClick={() => deletarTaxa(taxa.id)}
                              disabled={loading}
                              icon={Trash2}
                              variant="danger"
                              label="Excluir"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="p-8 text-center text-gray-500">
                        Nenhuma taxa encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPaginasTaxa > 1 && (
            <div className="px-6 py-4 border border-gray-200 rounded-lg bg-white">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Mostrando {indiceInicialTaxa + 1} ate {Math.min(indiceFinalTaxa, taxasOrdenadas.length)} de {taxasOrdenadas.length} registros
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPaginacaoTaxa((prev) => ({ ...prev, paginaAtual: 1 }))}
                    disabled={paginacaoTaxa.paginaAtual === 1}
                    className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Primeira página"
                  >
                    <ChevronsLeft size={16} />
                  </button>

                  <button
                    onClick={() =>
                      setPaginacaoTaxa((prev) => ({
                        ...prev,
                        paginaAtual: Math.max(1, prev.paginaAtual - 1),
                      }))
                    }
                    disabled={paginacaoTaxa.paginaAtual === 1}
                    className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Página anterior"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="flex space-x-1">
                    {gerarNumerosPaginas(paginacaoTaxa.paginaAtual, totalPaginasTaxa).map((numeroPagina) => (
                      <button
                        key={numeroPagina}
                        onClick={() =>
                          setPaginacaoTaxa((prev) => ({ ...prev, paginaAtual: numeroPagina }))
                        }
                        className={`px-3 py-2 rounded-md text-sm font-medium ${
                          paginacaoTaxa.paginaAtual === numeroPagina
                            ? "bg-blue-600 text-white"
                            : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {numeroPagina}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() =>
                      setPaginacaoTaxa((prev) => ({
                        ...prev,
                        paginaAtual: Math.min(totalPaginasTaxa, prev.paginaAtual + 1),
                      }))
                    }
                    disabled={paginacaoTaxa.paginaAtual === totalPaginasTaxa}
                    className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Próxima página"
                  >
                    <ChevronRight size={16} />
                  </button>

                  <button
                    onClick={() =>
                      setPaginacaoTaxa((prev) => ({ ...prev, paginaAtual: totalPaginasTaxa }))
                    }
                    disabled={paginacaoTaxa.paginaAtual === totalPaginasTaxa}
                    className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Última página"
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">
              {editandoId ? "Editar Maquina" : "Nova Maquina de Cartao"}
            </h2>

            <div className="space-y-4">
              <div>
                <FormField label="Nome" htmlFor="maquina-nome">
                <TextInput
                  id="maquina-nome"
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ nome: e.target.value })}
                  placeholder="Ex: Maquina 01"
                />
                </FormField>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                onClick={fecharForm}
                variant="ghost"
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                onClick={salvarMaquina}
                className="flex-1"
                disabled={loading}
              >
                {loading ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showFormTaxa && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">
              {editandoTaxaId ? "Editar Taxa" : "Nova Taxa"}
            </h2>

            <div className="space-y-4">
              <div>
                <FormField label="Maquina de Cartao" htmlFor="taxa-maquina">
                <SelectInput
                  id="taxa-maquina"
                  value={formTaxa.maquinaCartaoId}
                  onChange={(e) =>
                    setFormTaxa((prev) => ({ ...prev, maquinaCartaoId: e.target.value }))
                  }
                >
                  <option value="">Selecione</option>
                  {maquinas.map((maquina) => (
                    <option key={maquina.id} value={maquina.id}>
                      {maquina.nome}
                    </option>
                  ))}
                </SelectInput>
                </FormField>
              </div>

              <div>
                <FormField label="Valor da Taxa" htmlFor="taxa-valor">
                <TextInput
                  id="taxa-valor"
                  type="text"
                  value={formTaxa.valorTaxa}
                  onChange={(e) => {
                    const value = e.target.value;
                    const { formatted } = formatarValorReal(value);
                    setFormTaxa((prev) => ({ ...prev, valorTaxa: value ? formatted : "" }));
                  }}
                  placeholder="Ex: 2,50"
                />
                </FormField>
              </div>

              <div>
                <FormField label="Mês de Referência" htmlFor="taxa-mes-ref">
                <TextInput
                  id="taxa-mes-ref"
                  type="month"
                  value={formTaxa.mesRef}
                  onChange={(e) =>
                    setFormTaxa((prev) => ({ ...prev, mesRef: e.target.value }))
                  }
                />
                </FormField>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                onClick={fecharFormTaxa}
                variant="ghost"
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                onClick={salvarTaxa}
                className="flex-1"
                disabled={loading}
              >
                {loading ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default MaquinaCartao;
